/**
 * Copy for the shared inventory components under `components/Inventory` — the BOQ search, the line
 * table, its card-list equivalent, the totals footer and the item-picker dialog.
 *
 * Its own namespace rather than one flow's: the create-order and create-claim wizards both render
 * these components, and rooting the copy in `orders.createOrderRework` would have the claim flow
 * reading order copy. Wording that names the document being built ("Order total", "Selected on this
 * order") is deliberately *absent* here — those strings are passed in by the flow, from its own
 * namespace, so a claim says "claim". Everything below describes the BOQ or the control itself and
 * reads the same either way.
 */
export default {
  search: {
    placeholder: 'Search for an item',
    clear: 'Clear search',
    loading: "Loading the site's BOQ…",
    noBoqItems: 'This site’s BOQ has no items to add.',
    noMatches: 'No BOQ items match your search.',
    /** The control's accessible name — the visible label beside it is the short pair below. */
    selectAll: 'Select every listed line',
    clearSelection: 'Clear the selected lines',
    /**
     * What the select-all row reads, and the only label it has. It used to name the group
     * ("Matching BOQ lines at this site"), which described the list rather than the control the
     * whole row actually is — and the control is what a reader is deciding about.
     */
    selectAllLabel: 'Select all',
    allSelectedLabel: 'All selected',
    selectedCount: '{count} selected',
    addSelected: 'Add {count} selected item | Add {count} selected items',
    addSelectedLabel: 'Add selected items',
    addVariation: 'Add as a new line item',
    addVariationHint: "Not on this site's bill, so it raises a variation",
    /** The BOQ balance shown beside a line, e.g. "12 m² left". */
    remaining: '{quantity} {unit} left',
    remainingNoUnit: '{quantity} left',
    lockedPending: 'Pending variation',
    lockedAdded: 'Added',
    /**
     * Item-level and deliberately generic: the team rejected distinguishing a deleted vendor from
     * a deleted item, since a hard-deleted item can no longer be traced back to its vendor
     * (PLE-1266).
     */
    lockedUnavailable: 'Item no longer available',
    pendingVariationReason:
      'This item has a pending variation that needs to be approved or denied before it can be added.',
    alreadyAddedReason:
      'This item has already been added. Change its quantity in the table below instead.',
    /**
     * Says "added" rather than "ordered" for the same reason the rest of this namespace does: the
     * claim flow renders the same panel, and a claim is not an order. The second sentence names
     * the only way out — the flag is fixed by replacing the line on the BOQ, not from here.
     */
    unavailableReason:
      'This item is no longer available from its vendor, so it cannot be added. Replace it on the site’s BOQ to use a different item.',
  },

  table: {
    columns: {
      item: 'Item',
      section: 'BOQ section',
      type: 'Type',
      vendor: 'Vendor',
      unit: 'Unit',
      remaining: 'Remaining',
      quantity: 'Qty',
      rate: 'Rate',
      total: 'Total',
    },
    itemNamePlaceholder: 'Item name',
    sectionPlaceholder: 'Add section',
    sectionEmptyMessage: 'No matching sections',
    /** The pinned action under the section results: it offers to create what was searched for. */
    addSection: 'Add "{query}"',
    addSectionEmpty: 'Add a new section',
    findItem: 'Find an item',
    changeItem: 'Change item',
    vatRegistered: 'VAT registered',
    required: 'Required',
    cannotBeZero: 'Cannot be 0',
    resetRate: 'Reset to the BOQ rate',
    /**
     * The remaining-balance tooltip's rows. Both describe the BOQ rather than the document being
     * built — `alreadyOrdered` is what *other* orders have taken off this line's approved quantity,
     * which is the same figure whether the user is raising an order or a claim against it. The row
     * that does name the current document is passed in by the flow.
     */
    approved: 'Approved',
    alreadyOrdered: 'Already ordered',
    addAllRemaining: 'Add the remaining {quantity} of {name}',
    /** The card list's visible version: no hover there, so the shortcut has to name itself. */
    addAll: 'Add all {quantity}',
    /** The table's version of the same shortcut, explaining the click inside the tooltip. */
    addAllHint: 'Click to add all {quantity} to Qty',
    /** Names the line, so a screen reader hears which row's remove button this is. */
    removeItem: 'Remove {name}',
    variation: {
      // The banner is the label wrapped around whichever reasons apply, so a line that is both
      // over on quantity and above on rate says so in one strip rather than naming only the first.
      label: 'Variation · {reasons}',
      reasonSeparator: ', ',
      notOnBoq: 'not on the BOQ',
      quantityOverBoq: 'qty over BOQ',
      rateAboveBoq: 'rate above BOQ',
      rateBelowBoq: 'rate below BOQ',
      quantityCaption: '{quantity} over BOQ',
      rateCaption: 'BOQ rate {rate}',
    },
  },

  totals: {
    /**
     * What sits behind an "(incl VAT)" label: the rule that decides which lines carry VAT, then
     * the one figure a reader of that label is actually after — how much of the total is VAT.
     * The rest of the old itemised breakdown stays gone; a full split in a tooltip would only be
     * the breakdown relocated.
     */
    vatTooltip: {
      /**
       * One sentence, because the reader is checking a figure rather than learning the VAT rules.
       * It answers the only question the label raises — why this total includes VAT — and the
       * amount below it answers how much. The old wording spent a second sentence on the vendors
       * that carry none, which is the case where there is nothing to explain (PLE-1359).
       */
      applies: 'Includes VAT at {rate}% on items from VAT-registered vendors.',
      excludesVariations: 'Pending variations are not counted until they are approved.',
      /**
       * Carries the count, because the projected total's label is the only thing that says there
       * are pending variations at all — the panel no longer states it separately.
       */
      includesVariations:
        'The total if the pending variation is approved. | The total if all {count} pending variations are approved.',
      included: 'VAT included',
      /**
       * Takes the figure's place below the rule: the bottom row always answers "how much VAT?",
       * with an amount or with none. Showing R 0.00 there would explain an amount the document
       * does not have, and the rule directly above already says why there is none.
       *
       * "have been added", not "are included": included reads as *left out of the total*, as
       * though such items existed and were excluded. The fact is about the inventory — none were
       * ever added — so it borrows the verb the search and the line list already use.
       */
      noVatItems: 'No items from VAT-registered vendors have been added.',
    },
  },

  /**
   * The vendor-item dialog: picking a catalogue item for a line, or browsing the catalogue from the
   * Projects, Sites and Vendors pages. One namespace for both, because the list, the filters and the
   * custom-item form are the same in either.
   */
  selectItemDialog: {
    findTitle: 'Find an item',
    browseTitle: 'Search items',
    addCustomTitle: 'Add a custom item',
    back: 'Back to item search',
    addCustomIntro: 'Add an item to a vendor’s catalogue. It will be available on every bill.',
    filters: {
      search: 'Search items',
      type: 'Type',
      vendor: 'Vendor',
      searchVendors: 'Search vendors',
      clear: 'Clear filters',
      vendorCountHint: '{count} vendors. Search to narrow the list.',
      noVendorMatches: 'No vendors match your search',
    },
    columns: {
      item: 'Item',
      vendor: 'Vendor',
      type: 'Type',
      rate: 'Rate',
      lastPaid: 'Last paid',
    },
    results: {
      emptyTitle: 'No items found',
      emptyDescription: 'Try a different search, or clear the filters.',
      unsyncedHeading: 'From unsynced vendors',
      unsyncedDescription:
        'These vendors have items matching your search. Sync a vendor to use their catalogue.',
      unsynced: 'Unsynced',
      lastPaid: 'Last paid {amount}',
      perUnit: '{amount} / {unit}',
      noPermissionToSync: 'Ask your org admin, site admin or site PO to sync this vendor.',
      syncing: 'Syncing vendor…',
    },
    prompt: {
      cantFind: 'Can’t find the item you’re looking for?',
      addCustomItem: 'Add a custom item',
      addItemsToVendors: 'Add items to vendors',
      noPermission: 'Ask your site admin or procurement manager to add it.',
    },
    sync: {
      successTitle: 'Vendor synced',
      successContent: '{vendor} is now synced with your organisation.',
    },
    customItem: {
      vendorSection: 'Vendor',
      vendorMode: 'Vendor for this item',
      existingVendor: 'Existing vendor',
      newVendor: 'New vendor',
      vendor: 'Vendor',
      vendorName: 'Vendor name',
      contactEmail: 'Sales rep email',
      vatRegistered: 'VAT registered',
      vatNumber: 'VAT number',
      yes: 'Yes',
      no: 'No',
      itemSection: 'Item details',
      documentSection: 'Documentation',
      submit: 'Add item',
      cancel: 'Cancel',
    },
  },
};
